/**
 * What the platform billing screens decide, as pure functions.
 *
 * Kept out of the components for the same reason as `billingState.ts`: the
 * price wording, the access states and the redirect check can be tested
 * without mounting anything or driving Stripe.
 */
import { formatCents } from '@/lib/money'
import type {
    PlatformAccess,
    PlatformPlan,
    PlatformSubscriptionSummary
} from '@/types/PlatformBilling'

/** The Settings tab that holds the gym's Manajits plan. */
export const SUBSCRIPTION_TAB = 'subscription'

/** Where the Manajits plan lives in the SPA: a tab of Settings. */
export const SUBSCRIPTION_PATH = `/app/settings?tab=${SUBSCRIPTION_TAB}`

/** Whether a route is the Manajits plan tab of Settings. */
export function isSubscriptionRoute(route: {
    path: string
    query: Record<string, unknown>
}): boolean {
    return route.path === '/app/settings' && route.query.tab === SUBSCRIPTION_TAB
}

/**
 * Cents → a price for a plan card.
 *
 * Drops a whole-dollar `.00`, so the headline reads "$20" and not "$20.00".
 * A part-dollar amount keeps its cents: "$86.67".
 */
export function formatPlanPrice(cents: number): string {
    return formatCents(cents).replace(/\.00$/, '')
}

/** The large price on a plan card. Gyms think in weeks, so the weekly price leads. */
export function weeklyPriceLabel(plan: PlatformPlan): string {
    return formatPlanPrice(plan.weekly_amount_cents)
}

/**
 * The line under the weekly price.
 *
 * Stripe bills monthly, at weekly × 52 ÷ 12. Without this line, a gym that
 * reads "$20 a week" and then gets an $86.67 invoice thinks it was overcharged.
 */
export function billedMonthlyLabel(plan: PlatformPlan): string {
    return `Billed monthly at ${formatCents(plan.monthly_amount_cents)}`
}

/** How many students a plan covers, and what happens past that number. */
export function studentAllowanceLabel(plan: PlatformPlan): string {
    if (plan.has_student_limit || plan.overage_monthly_cents === null) {
        return `Up to ${plan.included_students} students`
    }

    return `${plan.included_students} students included, then ${formatCents(plan.overage_monthly_cents)} for each extra student each month`
}

/** Badge tones. Mirrors the `Tone` type inside Badge.vue. */
export type BadgeTone = 'success' | 'warning' | 'danger' | 'neutral' | 'primary'

const ACCESS_TONES: Record<PlatformAccess, BadgeTone> = {
    active: 'success',
    trialing: 'warning',
    past_due: 'danger',
    locked: 'danger',
    no_plan: 'danger'
}

/** The badge tone for an access state. Same colours as paid / pending / failed. */
export function accessTone(access: PlatformAccess | string | null | undefined): BadgeTone {
    return ACCESS_TONES[access as PlatformAccess] ?? 'neutral'
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Whole days left in the trial, rounded up.
 *
 * Rounded up because "0 days left" with half a day to go reads as "already
 * over". Never negative. Null when there is no trial date, or it does not parse.
 */
export function trialDaysLeft(
    trialEndsAt: string | null | undefined,
    now: Date = new Date()
): number | null {
    if (!trialEndsAt) return null

    const end = new Date(trialEndsAt).getTime()
    if (Number.isNaN(end)) return null

    return Math.max(0, Math.ceil((end - now.getTime()) / DAY_MS))
}

/** "3 days", "1 day", "today". For a sentence, not a heading. */
export function describeDaysLeft(days: number): string {
    if (days <= 0) return 'today'
    return `in ${days} ${days === 1 ? 'day' : 'days'}`
}

/** A date from the API for a sentence. "8 October 2026". Empty for a missing date. */
export function formatBillingDate(iso: string | null | undefined): string {
    if (!iso) return ''

    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''

    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * The usage bar's fill, 0 to 100.
 *
 * Capped at 100. A plan with overage can go past its included number, and a
 * bar wider than its track breaks the layout. Zero when nothing is included.
 */
export function usagePercent(count: number, included: number | null | undefined): number {
    if (!included || included <= 0) return 0
    return Math.min(100, Math.round((count / included) * 100))
}

/** The text beside the usage bar. */
export function usageLabel(summary: PlatformSubscriptionSummary): string {
    const { count, included, overage } = summary.students

    if (included === null) return `${count} ${count === 1 ? 'student' : 'students'}`

    const plan = summary.plan
    if (plan && !plan.has_student_limit && plan.overage_monthly_cents !== null) {
        return `${count} students: ${included} included + ${overage} extra × ${formatCents(plan.overage_monthly_cents)}`
    }

    return `${count} of ${included} students`
}

/** Whether a plan is too small for the gym now. The API refuses it with a 422. */
export function planTooSmall(plan: PlatformPlan, studentCount: number): boolean {
    return plan.has_student_limit && studentCount > plan.included_students
}

export interface SubscriptionBanner {
    tone: 'warning' | 'danger'
    message: string
}

/** Show the trial banner from this many days out. */
export const TRIAL_WARNING_DAYS = 3

/**
 * The app-wide banner for a gym that needs to act, or null when it does not.
 *
 * Only a trial close to its end, a failed payment, a lock or no plan at all.
 * A healthy gym sees nothing, so the banner keeps its meaning.
 */
export function subscriptionBanner(
    summary: PlatformSubscriptionSummary | null | undefined,
    now: Date = new Date()
): SubscriptionBanner | null {
    if (!summary) return null

    switch (summary.access) {
        case 'trialing': {
            const days = trialDaysLeft(summary.trial_ends_at, now)
            if (days === null || days > TRIAL_WARNING_DAYS) return null
            return {
                tone: 'warning',
                message: `Your free trial ends ${describeDaysLeft(days)}. Choose a plan to keep using Manajits.`
            }
        }
        case 'past_due':
            return {
                tone: 'danger',
                message:
                    'Your last Manajits payment failed. Update your payment details to keep full access.'
            }
        case 'locked':
            return {
                tone: 'danger',
                message: 'Your Manajits subscription is not active. Manajits is read-only.'
            }
        case 'no_plan':
            return {
                tone: 'danger',
                message: 'Your gym has no Manajits plan. Choose a plan to keep using Manajits.'
            }
        default:
            return null
    }
}

/** The only hosts a Stripe redirect may go to. */
const STRIPE_REDIRECT_PREFIXES = ['https://checkout.stripe.com/', 'https://billing.stripe.com/']

/**
 * Whether a redirect URL from the API is safe to send the browser to.
 *
 * Only Stripe's hosted Checkout and billing portal, or this SPA's own origin
 * (fake mode sends the browser straight back here). Anything else is refused,
 * so a bad or tampered response cannot send a gym owner to a look-alike
 * payment page.
 */
export function isAllowedRedirect(url: unknown, origin: string): boolean {
    if (typeof url !== 'string' || url === '') return false

    if (STRIPE_REDIRECT_PREFIXES.some((prefix) => url.startsWith(prefix))) return true

    try {
        // Absolute URLs only. A relative one resolves against nothing useful.
        return new URL(url).origin === origin
    } catch {
        return false
    }
}

/** The envelope an axios failure carries, as much of it as this needs. */
interface ErrorResponse {
    status?: number
    data?: { message?: unknown }
}

/**
 * The plan-full message from `POST /users`, or null for any other failure.
 *
 * A 409 here means the gym's plan has no room for another student. The API's
 * message names the plan and the number, so it is shown as written.
 */
export function planLimitMessage(error: unknown): string | null {
    const response = (error as { response?: ErrorResponse })?.response
    if (response?.status !== 409) return null

    const message = response.data?.message
    return typeof message === 'string' && message.length
        ? message
        : 'This gym has reached the student limit of its plan. Change plan to add more.'
}
